import { LuCircleAlert, LuX } from "react-icons/lu";

import CatalogueItemModal from "components/CatalogueItemModal";
import ThemedSelect from "components/FormControls/ThemedSelect";
import useUnsavedModalChanges from "hooks/useUnsavedModalChanges";
import "layouts/Quotation/quotation.css";

const fieldErrorMessages = {
  itemCode: "Enter an item code.",
  itemName: "Enter an item name.",
  itemPrice: "Enter a unit price.",
};

const unitOptions = ["each", "hour", "day", "item"];

const preventInvalidNumberKey = (event) => {
  if (["e", "E", "+", "-"].includes(event.key)) event.preventDefault();
};

export default function CatalogueItemDialog({
  accountOptions,
  dialogLabel = "Add new catalogue item",
  darkMode,
  error = "",
  fieldErrors = {},
  isSaving,
  onChange,
  onClose,
  onSubmit,
  submitDisabled = false,
  submitLabel = "Save item",
  title = "Add new item",
  values,
}) {
  const unsavedChanges = useUnsavedModalChanges({ values, onClose, busy: isSaving });
  const availableUnits =
    values.unit && !unitOptions.includes(values.unit)
      ? [values.unit, ...unitOptions]
      : unitOptions;
  const fieldError = (name) =>
    fieldErrors[name] || (error === name ? fieldErrorMessages[name] : "");
  const generalError =
    error && !Object.prototype.hasOwnProperty.call(fieldErrorMessages, error)
      ? error
      : "";

  const select = (name, label, options) => (
    <ThemedSelect
      dark={darkMode}
      aria-label={label}
      value={values[name]}
      onChange={(event) => onChange(name, event.target.value)}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </ThemedSelect>
  );

  return (
    <CatalogueItemModal
      portalDropdowns
      darkMode={darkMode}
      isSaving={isSaving}
      onClose={unsavedChanges.requestClose}
    >
      <section
        className="quotation-modal-dialog quotation-catalogue-modal"
        role="dialog"
        aria-modal="true"
        aria-label={dialogLabel}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="quotation-modal-header">
          <h3 id="catalogue-item-dialog-title">{title}</h3>
          <button
            type="button"
            className="quotation-dialog-close-btn"
            disabled={isSaving}
            onClick={unsavedChanges.requestClose}
            aria-label="Close"
          >
            <LuX />
          </button>
        </header>

        <div className="quotation-modal-body">
          {generalError && (
            <div className="quotation-modal-alert" role="alert">
              <LuCircleAlert />
              <span>{generalError}</span>
            </div>
          )}

          <div className="quotation-catalogue-row quotation-catalogue-row-three">
            <div className="quotation-field-wrap">
              <label htmlFor="catalogue-item-code">
                Item code <span className="quotation-required-star">*</span>
              </label>
              <div
                className={`quotation-input-box${
                  fieldError("itemCode") ? " quotation-input-error" : ""
                }`}
              >
                <input
                  id="catalogue-item-code"
                  placeholder="e.g. SRV-011"
                  value={values.itemCode}
                  onChange={(event) => onChange("itemCode", event.target.value)}
                />
              </div>
              {fieldError("itemCode") && (
                <small className="quotation-field-error">
                  {fieldError("itemCode")}
                </small>
              )}
            </div>

            <div className="quotation-field-wrap">
              <label htmlFor="catalogue-item-name">
                Item name <span className="quotation-required-star">*</span>
              </label>
              <div
                className={`quotation-input-box${
                  fieldError("itemName") ? " quotation-input-error" : ""
                }`}
              >
                <input
                  id="catalogue-item-name"
                  autoFocus
                  value={values.itemName}
                  onChange={(event) => onChange("itemName", event.target.value)}
                />
              </div>
              {fieldError("itemName") && (
                <small className="quotation-field-error">
                  {fieldError("itemName")}
                </small>
              )}
            </div>

            <div className="quotation-field-wrap">
              <label>Account</label>
              {select("categoryCodeName", "Account", accountOptions)}
            </div>
          </div>

          <div className="quotation-catalogue-row quotation-catalogue-row-two">
            <div className="quotation-field-wrap">
              <label htmlFor="catalogue-item-description">Description</label>
              <div className="quotation-input-box">
                <input
                  id="catalogue-item-description"
                  value={values.itemDescription}
                  onChange={(event) =>
                    onChange("itemDescription", event.target.value)
                  }
                />
              </div>
            </div>
            <div className="quotation-field-wrap">
              <label>Status</label>
              {select("status", "Status", [
                { value: "Active", label: "Active" },
                { value: "Inactive", label: "Inactive" },
              ])}
            </div>
          </div>

          <div className="quotation-catalogue-row quotation-catalogue-row-three">
            <div className="quotation-field-wrap">
              <label className="quotation-optional-label">
                Unit · optional
              </label>
              {select(
                "unit",
                "Unit",
                availableUnits.map((unit) => ({ value: unit, label: unit }))
              )}
            </div>
            <div className="quotation-field-wrap">
              <label htmlFor="catalogue-item-price">
                Unit price <span className="quotation-required-star">*</span>
              </label>
              <div
                className={`quotation-input-box quotation-money-input${
                  fieldError("itemPrice") ? " quotation-input-error" : ""
                }`}
              >
                <span>$</span>
                <input
                  id="catalogue-item-price"
                  aria-label="Unit price"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={values.itemPrice}
                  onKeyDown={preventInvalidNumberKey}
                  onChange={(event) =>
                    onChange("itemPrice", event.target.value)
                  }
                />
              </div>
              {fieldError("itemPrice") && (
                <small className="quotation-field-error">
                  {fieldError("itemPrice")}
                </small>
              )}
            </div>
            <div className="quotation-field-wrap">
              <label htmlFor="catalogue-item-quantity">Default quantity</label>
              <div className="quotation-input-box">
                <input
                  id="catalogue-item-quantity"
                  type="number"
                  min="0"
                  step="1"
                  value={values.defaultQuantity}
                  onKeyDown={preventInvalidNumberKey}
                  onChange={(event) =>
                    onChange("defaultQuantity", event.target.value)
                  }
                />
              </div>
            </div>
          </div>

          <div className="quotation-catalogue-row quotation-catalogue-row-three">
            <div className="quotation-field-wrap">
              <label>GST</label>
              {select("gst", "GST", [
                { value: 10, label: "10%" },
                { value: 0, label: "0%" },
              ])}
            </div>
            <div className="quotation-field-wrap">
              <label>Default discount</label>
              {select("discountType", "Default discount", [
                { value: "None", label: "None" },
                { value: "Percentage", label: "Percentage" },
                { value: "Fixed", label: "Fixed amount" },
              ])}
            </div>
            <div
              className={`quotation-field-wrap${
                values.discountType === "None"
                  ? " quotation-field-disabled"
                  : ""
              }`}
            >
              <label htmlFor="catalogue-item-discount">Discount value</label>
              <div className="quotation-input-box">
                <input
                  id="catalogue-item-discount"
                  type="number"
                  min="0"
                  disabled={values.discountType === "None"}
                  value={values.discountValue}
                  onKeyDown={preventInvalidNumberKey}
                  onChange={(event) =>
                    onChange("discountValue", event.target.value)
                  }
                />
              </div>
            </div>
          </div>
        </div>

        <footer className="quotation-modal-footer">
          <button
            type="button"
            className="quotation-modal-cancel-btn"
            disabled={isSaving}
            onClick={unsavedChanges.requestClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="quotation-modal-submit-btn"
            disabled={isSaving || submitDisabled}
            onClick={onSubmit}
          >
            {isSaving ? "Saving..." : submitLabel}
          </button>
        </footer>
      </section>
      {unsavedChanges.dialog}
    </CatalogueItemModal>
  );
}
